import { Editor } from "@tinymce/tinymce-react";
import { Editor as EditorArg } from "tinymce";
import {
  useEffect,
  useRef,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  playStatusAtom,
  selectedTextForPlayAtom,
  setEditorValueAtom,
  userInputAtom,
  userPreferencesAtom,
  activeSSMLTagsAtom,
  isHtmlViewAtom,
  setNotificationAtom,
  isSsmlViewEditable,
  authTokenAtom,
  selectedValuesAtom,
  validationErrorsAtom,
  voicesDescriptionAtom,
  styleEmotionNames,
  languageOptionsAtom,
} from "atoms";
import { useTranslation } from "react-i18next";
import { PlayStatus } from "shared/models";
import { ListOfVoices } from "@generated/list_of_voices";

import CustomToolbar from "./CustomToolbar";
import { editorKeyDownHandler } from "./utils/editorKeyDownHandler";
import { transformToSSML } from "shared/utils/ssml-helper";
import {
  decodeUmlautEntitiesOnly,
  stripEditorHTML,
} from "shared/utils/tts-editor-helper";
import { SSMLNestingBanner } from "./components/SSMLNestingBanner";
import { SSMLViewBanner } from "./components/SSMLViewBanner";
import { useSelectionBanner } from "./hooks/useSelectionBanner";
import { pastePreprocessHandler } from "./utils/pastePreprocess";
import { onEditorKeypress } from "./utils/keypressHandler";
import { onEditorEnter } from "./utils/enterHandler";
import { MenuBorderColor, SSMLTag } from "shared/constants";
import { useSSMLMenuDisabled } from "shared/utils/menu-disabling-rules";
import { validateSSML } from "./utils/ssmlValidator";
import ValidationFailureNotification from "./components/ValidationFailureNotification";
import ValidationSuccessfulNotification from "./components/ValidationSuccessfulNotification";
import { convertRawSSMLToTextFormat } from "./utils/convertRawSSMLToTextFormat";
import { fetchVoicesExtended } from "shared/utils/tts";
import ErrorBox from "../components/ErrorBox";

interface TTSCustomEditorProps {
  handleStopClick: () => void;
  editorContent: string;
  onEditorChange: (content: string) => void;
  selectedVoice?: string;
}

export interface TTSCustomEditorHandle {
  applyColoredSSML: () => void;
  insertExample: (ssml: string) => void;
  isEmpty: () => boolean;
}

const TTSCustomEditor = forwardRef<TTSCustomEditorHandle, TTSCustomEditorProps>(
  (props, ref) => {
    const { handleStopClick, editorContent, onEditorChange, selectedVoice } =
      props;
    const { t } = useTranslation();
    const userPreferences = useAtomValue(userPreferencesAtom);
    const token = useAtomValue(authTokenAtom);
    const { selectedEnv } = useAtomValue(selectedValuesAtom);
    const editorRef = useRef<Editor | null>(null);
    const setUserInput = useSetAtom(userInputAtom);
    const [playStatus, setPlayStatus] = useAtom(playStatusAtom);
    const setSelectedTextForPlay = useSetAtom(selectedTextForPlayAtom);
    const setEditorValue = useSetAtom(setEditorValueAtom);
    const setActiveSSMLTags = useSetAtom(activeSSMLTagsAtom);
    const [isHtmlView, setIsHtmlView] = useAtom(isHtmlViewAtom);
    const setNotification = useSetAtom(setNotificationAtom);
    const setSsmlViewEditable = useSetAtom(isSsmlViewEditable);
    const setValidationErrors = useSetAtom(validationErrorsAtom);

    const voicesDescription = useAtomValue(voicesDescriptionAtom);
    const voiceNames = voicesDescription.map((v) => v.voiceName);
    const emotionNames = useAtomValue(styleEmotionNames);
    const languageOptions = useAtomValue(languageOptionsAtom);
    const languageValues = useMemo(
      () => languageOptions.map((o) => o.value),
      [languageOptions]
    );

    const [isEditorReady, setIsEditorReady] = useState(false);
    const [anyMenuDisabled, setAnyMenuDisabled] = useState(false);
    const [showSSMLBanner, setShowSSMLBanner] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [originalContent, setOriginalContent] = useState("");
    const [showSSMLNotification, setShowSSMLNotification] = useState(false);
    const [showSuccessNotification, setShowSuccessNotification] =
      useState(false);
    const [coloredSSML, setColoredSSML] = useState("");
    const [listOfVoices, setListOfVoices] = useState<
      ListOfVoices | undefined
    >();
    const ssmlViewRef = useRef<boolean>(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const currentSelectionRef = useRef<string>("");

    const { selectionState, checkSelectionInsideTag } =
      useSelectionBanner(editorRef);

    useEffect(() => {
      if (!token) return;
      const envName = selectedEnv;
      void fetchVoicesExtended(envName, token).then(setListOfVoices);
    }, [token, selectedEnv]);

    const updateEditorBorderWithMenuState = useCallback(() => {
      const editorContainer = document.querySelector(
        ".tox-tinymce"
      ) as HTMLElement;
      if (editorContainer) {
        // Determine border color based on selection state OR menu disabled state
        const borderColor =
          selectionState !== "none" || anyMenuDisabled
            ? "rgba(2, 136, 209, 1)"
            : MenuBorderColor;
        editorContainer.style.border = `1px solid ${borderColor}`;
        editorContainer.style.borderTop = "0";
        editorContainer.style.borderRadius = "0";
      }
    }, [selectionState, anyMenuDisabled]);

    const isVoiceDisabled = useSSMLMenuDisabled(
      isEditorReady ? editorRef : { current: null },
      SSMLTag.Voice
    );
    const isProsodyDisabled = useSSMLMenuDisabled(
      isEditorReady ? editorRef : { current: null },
      SSMLTag.Prosody
    );
    const isEmphasisDisabled = useSSMLMenuDisabled(
      isEditorReady ? editorRef : { current: null },
      SSMLTag.Emphasis
    );
    const isSayAsDisabled = useSSMLMenuDisabled(
      isEditorReady ? editorRef : { current: null },
      SSMLTag.SayAs
    );

    useEffect(() => {
      setShowSSMLBanner(isHtmlView);
      if (!isHtmlView) setIsEditing(false);
    }, [isHtmlView]);

    // Update editor readonly state when isEditing changes
    useEffect(() => {
      if (isEditorReady && editorRef.current?.editor) {
        const editor = editorRef.current.editor;
        if (isHtmlView) {
          editor.mode.set(isEditing ? "design" : "readonly");
        } else {
          editor.mode.set("design");
        }
      }
    }, [isEditing, isHtmlView, isEditorReady]);

    useEffect(() => {
      if (isEditorReady && editorRef.current?.editor) {
        const newAnyMenuDisabled =
          isVoiceDisabled ||
          isProsodyDisabled ||
          isEmphasisDisabled ||
          isSayAsDisabled;

        setAnyMenuDisabled(newAnyMenuDisabled);
      }
    }, [
      isEditorReady,
      isVoiceDisabled,
      isProsodyDisabled,
      isEmphasisDisabled,
      isSayAsDisabled,
      selectionState,
      editorContent,
    ]);

    // Determine when to show the banner
    const shouldShowNestingBanner =
      selectionState !== "none" || anyMenuDisabled;

    useEffect(() => {
      const rafId = requestAnimationFrame(updateEditorBorderWithMenuState);
      return () => cancelAnimationFrame(rafId);
    }, [
      selectionState,
      shouldShowNestingBanner,
      updateEditorBorderWithMenuState,
    ]);

    useEffect(() => {
      cleanTinyMCEContent();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHtmlView]);

    useEffect(() => {
      if (!editorContent)
        editorRef.current?.editor
          ?.getBody()
          ?.setAttribute("data-mce-placeholder", t("tts.editor.placeholder"));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPreferences.language]);

    useEffect(() => {
      if (playStatus === PlayStatus.InProgress) {
        setSelectedTextForPlay(getInputText(true));
        setPlayStatus(PlayStatus.Ready);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playStatus]);

    // Force re-evaluation of menu states when selection changes
    useEffect(() => {
      if (isEditorReady) {
        const timer = setTimeout(() => {
          setActiveSSMLTags((prev) => [...prev]);
        }, 0);

        return () => clearTimeout(timer);
      }
    }, [selectionState, isEditorReady, setActiveSSMLTags]);

    useEffect(() => {
      ssmlViewRef.current = isHtmlView;
    }, [isHtmlView]);

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        applyColoredSSML: () => {
          const editor = editorRef.current?.editor;
          if (!editor || !isHtmlView) return;

          const ssmlText = editor.getContent({ format: "text" });
          const valid = validateSSML(ssmlText, false, t, {
            dynamicAllowedValues: {
              voiceNames,
              styleNames: emotionNames,
              languageValues,
            },
          });

          if (valid.valid) {
            // Convert plain SSML back to data-ssml format
            const { converted } = convertRawSSMLToTextFormat(ssmlText);
            const transformedContent = transformToSSML(converted, true);
            editor.setContent(transformedContent);
            // editor.getBody().setAttribute("contenteditable", "true");
            setColoredSSML(transformedContent);
          }
        },
        insertExample: (ssml: string) => {
          if (isHtmlView) {
            setIsHtmlView(false);
            setIsEditing(false);
            setSsmlViewEditable(false);
          }

          const stripped = ssml
            .trim()
            .replace(/^<speak[^>]*>/, "")
            .replace(/<\/speak>\s*$/, "")
            .trim();

          const { converted } = convertRawSSMLToTextFormat(stripped, true);

          const editor = editorRef.current?.editor;
          if (editor) {
            const currentContent = editor.getContent();
            // If editor is empty (just an empty <p> or blank), replace; otherwise append
            const isEmpty =
              !currentContent ||
              currentContent === "<p></p>" ||
              currentContent === "<p><br></p>" ||
              currentContent.trim() === "";

            if (isEmpty) {
              editor.setContent(`<p>${converted}</p>`);
              onEditorChange(`<p>${converted}</p>`);
            } else {
              const newContent = `${currentContent}<p>${converted}</p>`;
              editor.setContent(newContent);
              onEditorChange(newContent);
            }
          } else {
            onEditorChange(stripped);
          }
        },
        isEmpty: () => {
          const editor = editorRef.current?.editor;
          if (!editor) return true;
          return !editor.getContent({ format: "text" }).trim();
        },
      }),
      [
        isHtmlView,
        voiceNames,
        emotionNames,
        languageValues,
        t,
        setIsHtmlView,
        setSsmlViewEditable,
        onEditorChange,
      ]
    );

    useEffect(() => {
      if (!isEditorReady || !editorRef.current?.editor) return;

      const editor = editorRef.current.editor;

      const updateSelection = () => {
        if (isHtmlView) {
          const editorWin = editor.getWin();

          if (editorWin) {
            const editorSelection = editorWin.getSelection();

            if (editorSelection) {
              const selectedText = editorSelection.toString();

              if (selectedText) {
                currentSelectionRef.current = selectedText;
              } else {
                currentSelectionRef.current = "";
              }
            }
          }
        } else {
          currentSelectionRef.current = "";
        }
      };

      editor.on("SelectionChange", updateSelection);
      editor.on("mouseup", updateSelection);
      editor.on("keyup", updateSelection);

      return () => {
        editor.off("SelectionChange", updateSelection);
        editor.off("mouseup", updateSelection);
        editor.off("keyup", updateSelection);
      };
    }, [isEditorReady, isHtmlView]);
    /**
     * Generate the input text based on the htmlView atom value
     * If it's already html, then read as text as set it. Else transform and set it.
     * Also, check if only selected text should be considered based on the boolean input.
     */
    const getInputText = useCallback(
      (isSelected: boolean) => {
        const editor = editorRef.current?.editor;
        if (!editor) return "";

        if (isHtmlView) {
          if (isSelected && currentSelectionRef.current) {
            return currentSelectionRef.current;
          }

          const editorTextContent = isSelected
            ? editor.selection.getContent({ format: "text" })
            : editor.getContent({ format: "text" });
          return editorTextContent || "";
        } else {
          let editorHtmlContent = isSelected
            ? editor.selection.getContent({ format: "html" })
            : editor.getContent({ format: "html" });

          if (isSelected) {
            const selectionNode = editor.selection.getNode();
            const hasParentSSMLTag = selectionNode.closest("span[data-ssml]");

            if (!editorHtmlContent.includes("data-ssml") && hasParentSSMLTag) {
              const ssmlData = hasParentSSMLTag.getAttribute("data-ssml");
              const tempWrapper = document.createElement("span");
              tempWrapper.setAttribute("data-ssml", ssmlData || "");
              tempWrapper.innerHTML = editorHtmlContent;
              editorHtmlContent = tempWrapper.outerHTML;
            }
          }

          if (editorHtmlContent?.includes("<p>")) {
            editorHtmlContent = editorHtmlContent.substring(
              3,
              editorHtmlContent.length - 4
            );
          }

          const result = transformToSSML(
            decodeUmlautEntitiesOnly(editorHtmlContent || ""),
            false
          );
          const validationOutcome = validateSSML(result, true, t, {
            dynamicAllowedValues: {
              voiceNames,
              styleNames: emotionNames,
              languageValues,
            },
          });
          const validatedResult = validationOutcome.valid
            ? result
            : (validationOutcome.sanitizedSSML ?? result);

          return validatedResult || "";
        }
      },
      [isHtmlView, voiceNames, emotionNames, languageValues, t]
    );

    const cleanTinyMCEContent = useCallback(() => {
      setUserInput(getInputText(false));
    }, [getInputText, setUserInput]);

    const handleEditorChange = useCallback(
      (content: string) => {
        onEditorChange(content);
        setEditorValue(() => content);
        cleanTinyMCEContent();
        handleStopClick();
      },
      [onEditorChange, setEditorValue, cleanTinyMCEContent, handleStopClick]
    );

    const handleStartEditing = useCallback(() => {
      const editor = editorRef.current?.editor;
      if (!editor) return;

      editor.setContent(coloredSSML);
      editor.getBody().setAttribute("contenteditable", "false");
      setSsmlViewEditable(true);
      setIsEditing(true);
    }, [coloredSSML, setSsmlViewEditable]);

    const handleClear = () => {
      setOriginalContent("");
      setValidationErrors([]);
    };

    // Handler for "Return to SSML View" button - just close notification
    const handleStayInSSML = useCallback(() => {
      setShowSSMLNotification(false);
      // User stays in SSML view with their current (invalid) content
    }, []);

    // Handler for "Switch to Visual Mode" button - discard changes and switch
    const handleSwitchToVisual = useCallback(() => {
      setValidationErrors([]);
      const editor = editorRef.current?.editor;
      if (!editor) return;
      setShowSSMLNotification(false);

      // Restore the original visual content (before SSML editing)
      editor.setContent(originalContent);
      editor.getBody().setAttribute("contenteditable", "true");
      onEditorChange(originalContent);
      // Switch back to visual view
      setIsHtmlView(false);
      setIsEditing(false);
    }, [originalContent, onEditorChange, setIsHtmlView, setValidationErrors]);

    // Handler to close success notification and stay in SSML view
    const handleSuccessNotificationClose = useCallback(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShowSuccessNotification(false);
    }, []);

    // Show success notification on validation success
    const handleToggleHtmlView = useCallback(() => {
      const editor = editorRef.current?.editor;
      if (!editorRef.current || !editor) return;

      if (isHtmlView) {
        // Switching FROM SSML view TO visual view
        if (isEditing) {
          const ssmlContent = editor.getContent({ format: "text" });
          const validationResult = validateSSML(ssmlContent, false, t, {
            dynamicAllowedValues: {
              voiceNames,
              styleNames: emotionNames,
              languageValues,
            },
          });
          if (!validationResult.valid) {
            // Show the ValidationFailureNotification dialog
            setValidationErrors(validationResult.errors);
            setShowSSMLNotification(true);
            return;
          }
          setValidationErrors([]);
          const { converted, hasInvalidTag } =
            convertRawSSMLToTextFormat(ssmlContent);
          if (hasInvalidTag) {
            setShowSSMLNotification(true);
            return;
          }
          // Validation passed - show success notification
          setShowSuccessNotification(true);

          // Auto-close notification and switch views after a short delay
          timeoutRef.current = setTimeout(() => {
            setShowSuccessNotification(false);
            // Switch to visual mode
            editor.setContent(converted, { format: "html" });
            editor.getBody().setAttribute("contenteditable", "true");
            onEditorChange(converted);
            setIsHtmlView(false);
            setIsEditing(false);
            setSsmlViewEditable(false);
          }, 1500);

          return;
        } else {
          editor.setContent(originalContent);
          editor.getBody().setAttribute("contenteditable", "true");
          onEditorChange(originalContent);
        }
      } else {
        // Switching FROM visual view TO SSML view
        const currentContent = editor.getContent({ format: "html" });
        setOriginalContent(currentContent);

        // Transform to SSML
        const transformedContent = transformToSSML(currentContent, true);
        setColoredSSML(transformedContent);
        const finalSSML = stripEditorHTML(transformedContent);
        editor.setContent(finalSSML);
        editor.getBody().setAttribute("contenteditable", "false");
      }

      setIsHtmlView(!isHtmlView);
    }, [
      isHtmlView,
      isEditing,
      originalContent,
      setIsHtmlView,
      onEditorChange,
      setSsmlViewEditable,
      setValidationErrors,
      voiceNames,
      emotionNames,
      languageValues,
      t,
    ]);
    
    const isInsideOnlyStylingSpan = (
      editor: EditorArg,
      key?: string
    ): boolean => {
      const sel = editor.selection;
      if (!sel) return false;
      const rng = sel.getRng();
      const container = rng.startContainer;
      const element =
        container.nodeType === Node.TEXT_NODE
          ? container.parentElement
          : (container as HTMLElement);

      const label = element?.closest("[only-styling]");
      if (!label) return false;

      if (container.nodeType !== Node.TEXT_NODE) return true;

      const wrapper = label.closest("[data-ssml]");
      if (!wrapper) return true;

      const isPrintable = !!key && key.length === 1;
      if (!isPrintable) return true;

      const labels = wrapper.querySelectorAll(":scope > [only-styling]");
      const openingLabel = labels[0] ?? null;
      const closingLabel = labels[labels.length - 1] ?? null;
      const len = container.textContent?.length ?? 0;

      if (label === openingLabel && rng.startOffset === 0) return false;
      if (label === closingLabel && rng.startOffset === len) return false;

      return true;
    };

    const isInVoiceOrStyleTag = (editor: EditorArg, key: string): boolean => {
      const sel = editor.selection;
      const rng = sel.getRng();
      if (!rng.collapsed) return false;

      const container = rng.startContainer;
      const offset = rng.startOffset;

      const voiceOrStyleWrapperOf = (n: Node | null): HTMLElement | null => {
        const el =
          n && n.nodeType === Node.TEXT_NODE
            ? n.parentElement
            : (n as HTMLElement | null);
        const wrapper = el?.closest("[data-ssml]") as HTMLElement | null;
        const ds = wrapper?.getAttribute("data-ssml") ?? "";
        return ds.startsWith("voice-") || ds.startsWith("style-")
          ? wrapper
          : null;
      };

      const isVoiceOrStyleWrapper = (n: Node | null): boolean =>
        !!voiceOrStyleWrapperOf(n);

      const elementOf = (n: Node | null): HTMLElement | null =>
        n && n.nodeType === Node.TEXT_NODE
          ? n.parentElement
          : (n as HTMLElement | null);

      const onLabel = elementOf(container)?.closest("[only-styling]");
      if (onLabel && isVoiceOrStyleWrapper(onLabel)) return true;

      if (container.nodeType === Node.TEXT_NODE) {
        const wrapper = voiceOrStyleWrapperOf(container);
        if (wrapper) {
          const len = container.textContent?.length ?? 0;
          const labels = wrapper.querySelectorAll(":scope > [only-styling]");
          const openingLabel = labels[0] ?? null;
          const closingLabel = labels[labels.length - 1] ?? null;
          const prev = (container as ChildNode).previousSibling;
          const next = (container as ChildNode).nextSibling;

          if (
            key === "Backspace" &&
            offset === 0 &&
            prev &&
            prev === openingLabel
          ) {
            return true;
          }
          if (
            key === "Delete" &&
            offset === len &&
            next &&
            next === closingLabel
          ) {
            return true;
          }
        }
      }

      if (container.nodeType === Node.TEXT_NODE) {
        const len = container.textContent?.length ?? 0;
        const insideVoiceOrStyle = !!voiceOrStyleWrapperOf(container);
        if (!insideVoiceOrStyle) {
          if (key === "Backspace" && offset === 0) {
            return isVoiceOrStyleWrapper(
              (container as ChildNode).previousSibling
            );
          }
          if (key === "Delete" && offset === len) {
            return isVoiceOrStyleWrapper(
              (container as ChildNode).nextSibling
            );
          }
        }
      } else if (container.nodeType === Node.ELEMENT_NODE) {
        const el = container as Element;
        if (key === "Backspace" && offset > 0) {
          return isVoiceOrStyleWrapper(el.childNodes.item(offset - 1));
        }
        if (key === "Delete") {
          return isVoiceOrStyleWrapper(el.childNodes.item(offset));
        }
      }

      return false;
    };

    return (
      <>
        <CustomToolbar
          editorRef={editorRef}
          isSelectionInsideTag={selectionState !== "none"}
          anyMenuDisabled={anyMenuDisabled}
          handleToggleHtmlView={handleToggleHtmlView}
          onClear={handleClear}
          listOfVoices={listOfVoices}
          selectedVoice={selectedVoice}
        />
        <div style={{ position: "relative" }}>
          <Editor
            id="tts-custom-editor-textarea"
            ref={editorRef}
            value={editorContent}
            tinymceScriptSrc="tinymce/tinymce.min.js"
            init={{
              content_css: "tinymce/css/tinymce-styles.css",
              content_style: `
              body, pre, code { 
                text-align: left; 
                line-height: 24px; 
                font-size: 16px; 
                font-family: 'IBM Plex Sans', sans-serif; 
                color: rgba(0, 0, 0, 0.87);
              }
              .mceNonEditable { cursor: default; }
              span[data-ssml] {
                display: inline !important;
                white-space: normal;
                word-break: break-word;
                overflow-wrap: break-word;
              }
              span[data-ssml][style*="border-bottom"],
              span[data-ssml*="prosody"],
              span[data-ssml*="voice"] {
                display: inline !important;
                width: auto !important;
              }
              span[data-ssml] span[data-ssml] {
                display: inline !important;
              }
              span[data-ssml*="phoneme-"],
              span[data-ssml*="voice-"],
              span[data-ssml*="style-"] {
                display: inline-block !important;
                margin: 0 1px;
              }
              span[only-styling] {
                cursor: text;
                user-select: text;
              }
            `,
              nowrap: false,
              branding: false,
              elementpath: false,
              statusbar: false,
              height: window.innerWidth <= 600 ? 180 : 352,
              menubar: false,
              toolbar: false,
              valid_elements: "*[*]",
              extended_valid_elements:
                "voice,span[class|style|data-ssml|contenteditable|only-styling]",
              custom_elements:
                "voice,~span[class|style|data-ssml|contenteditable|only-styling]",
              noneditable_noneditable_class: "mceNonEditable",
              object_resizing: false,
              placeholder: t("tts.editor.placeholder"),
              paste_preprocess: (
                plugin: EditorArg,
                args: { content: string }
              ) => {
                pastePreprocessHandler(
                  plugin,
                  args,
                  setNotification,
                  t,
                  ssmlViewRef.current
                );
              },
              setup: (editor: EditorArg) => {
                editor.on("BeforeExecCommand", (e) => {
                  if (e.command === "Undo" && ssmlViewRef.current) {
                    e.preventDefault();
                    return false;
                  }
                });
                editor.addCommand("Undo", () => {
                  if (ssmlViewRef.current) {
                    return false;
                  }
                  editor.undoManager.undo();
                });
                editor.on("init", () => {
                  const editorContainer = document.querySelector(
                    ".tox-tinymce"
                  ) as HTMLElement;
                  if (editorContainer) {
                    editorContainer.style.borderTop = "0";
                    editorContainer.style.borderRadius = "0";
                    editorContainer.setAttribute("aria-hidden", "false");
                  }
                  updateEditorBorderWithMenuState();
                  checkSelectionInsideTag();
                  setIsEditorReady(true);
                });

                // Handle window resize to adjust editor height
                const handleResize = () => {
                  const newHeight = window.innerWidth <= 600 ? 200 : 352;
                  const container = editor.getContainer();
                  if (container) {
                    container.style.height = `${newHeight}px`;
                  }
                  const editorBody = editor.getBody();
                  if (editorBody && editorBody.parentElement) {
                    editorBody.parentElement.style.height = `${newHeight}px`;
                  }
                };
                window.addEventListener("resize", handleResize);

                editor.on("remove", () => {
                  window.removeEventListener("resize", handleResize);
                });

                editor.on("keydown", (event) => {
                  editorKeyDownHandler(event, editor, setActiveSSMLTags);
                  onEditorEnter(event as unknown as KeyboardEvent, editor);

                  if (
                    event.key === "Backspace" ||
                    event.key === "Delete"
                  ) {
                    if (isInVoiceOrStyleTag(editor, event.key)) {
                      event.preventDefault();
                      event.stopPropagation();
                      return;
                    }
                  }

                  // prevent any actual editing (typing, deleting, pasting).
                  if (isInsideOnlyStylingSpan(editor, event.key)) {
                    // Allow navigation keys and modifier combos (copy etc.) to pass through
                    const isNavigationKey = [
                      "ArrowLeft",
                      "ArrowRight",
                      "ArrowUp",
                      "ArrowDown",
                      "Home",
                      "End",
                      "PageUp",
                      "PageDown",
                    ].includes(event.key);
                    const isModifierOnly =
                      event.key === "Control" ||
                      event.key === "Meta" ||
                      event.key === "Alt" ||
                      event.key === "Shift";
                    const isCopyShortcut =
                      (event.ctrlKey || event.metaKey) &&
                      ["c", "a"].includes(event.key.toLowerCase());

                    if (
                      !isNavigationKey &&
                      !isModifierOnly &&
                      !isCopyShortcut
                    ) {
                      event.preventDefault();
                      event.stopPropagation();
                      return;
                    }
                  }

                  //Code to stop color inheritance
                  if (
                    event.key.length === 1 &&
                    !event.ctrlKey &&
                    !event.metaKey &&
                    !event.altKey
                  ) {
                    const sel = editor.selection;
                    const rng = sel.getRng();

                    if (rng.collapsed) {
                      const container = rng.startContainer;
                      const ssmlAncestor = (
                        container.nodeType === Node.TEXT_NODE
                          ? container.parentElement
                          : (container as HTMLElement)
                      )?.closest("[data-ssml]");

                      if (ssmlAncestor) {
                        event.preventDefault();
                        const textNode = editor
                          .getDoc()
                          .createTextNode(event.key);

                        const wrapper = ssmlAncestor as HTMLElement;
                        const label = (
                          container.nodeType === Node.TEXT_NODE
                            ? container.parentElement
                            : (container as HTMLElement)
                        )?.closest("[only-styling]");
                        const labels = wrapper.querySelectorAll(
                          ":scope > [only-styling]"
                        );
                        const openingLabel = labels[0] ?? null;
                        const closingLabel =
                          labels[labels.length - 1] ?? null;
                        const len = container.textContent?.length ?? 0;

                        const atLeftEdge =
                          !!label &&
                          label === openingLabel &&
                          rng.startOffset === 0;
                        const atRightEdge =
                          !!label &&
                          label === closingLabel &&
                          rng.startOffset === len;

                        if (atLeftEdge) {
                          wrapper.parentNode?.insertBefore(textNode, wrapper);
                        } else if (atRightEdge) {
                          wrapper.parentNode?.insertBefore(
                            textNode,
                            wrapper.nextSibling
                          );
                        } else {
                          rng.insertNode(textNode);
                        }

                        const newRng = editor.dom.createRng();
                        newRng.setStart(textNode, 1);
                        newRng.collapse(true);
                        sel.setRng(newRng);
                        return;
                      }
                    }

                    const hasSelection = !rng.collapsed;
                    const isBodySelected =
                      rng.startContainer === editor.getBody() ||
                      rng.endContainer === editor.getBody();
                    const selectedContent = sel.getContent();

                    if (
                      hasSelection &&
                      (isBodySelected || selectedContent.length > 10)
                    ) {
                      event.preventDefault();
                      editor.setContent(
                        `<p><span style="color: rgba(0, 0, 0, 0.87);">${event.key}</span></p>`
                      );
                      const newRng = editor.dom.createRng();
                      const firstP = editor.getBody().firstChild;
                      const firstSpan = firstP?.firstChild;
                      if (firstSpan?.firstChild) {
                        newRng.setStart(firstSpan.firstChild, 1);
                        newRng.collapse(true);
                        sel.setRng(newRng);
                      }
                      return;
                    }

                    const container = rng.startContainer;

                    if (container.nodeType === 3) {
                      const text = container.textContent || "";
                      const offset = rng.startOffset;
                      const beforeCursor = text.substring(0, offset);
                      const isAfterClosingTag =
                        /<\/[^>]+>$/.test(beforeCursor.trim()) ||
                        /\/>$/.test(beforeCursor.trim());

                      if (isAfterClosingTag) {
                        let coloredParent: HTMLElement | null = null;
                        let current: Node | null = container.parentNode;
                        while (current && current !== editor.getBody()) {
                          if (current.nodeType === 1) {
                            const elem = current as HTMLElement;
                            const color = elem.style.color;
                            if (
                              color &&
                              color !== "rgba(0, 0, 0, 0.87)" &&
                              color !== "rgb(0, 0, 0)"
                            ) {
                              coloredParent = elem;
                              break;
                            }
                          }
                          current = current.parentNode;
                        }
                        if (coloredParent) {
                          event.preventDefault();
                          editor.execCommand(
                            "mceInsertContent",
                            false,
                            `<span style="color: rgba(0, 0, 0, 0.87);">${event.key}</span>`
                          );
                          return;
                        }
                      }
                    }
                  }
                  // Backspace handler
                  if (event.key === "Backspace") {
                    const sel = editor.selection;
                    const rng = sel.getRng();
                    if (!rng.collapsed) return;
                    const container: Node = rng.startContainer;
                    const offset: number = rng.startOffset;

                    if (container.nodeType === Node.TEXT_NODE) {
                      const parent = container.parentNode as HTMLElement | null;
                      if (
                        parent?.style?.color === "rgba(0, 0, 0, 0.87)" ||
                        parent?.style?.color === "rgb(0, 0, 0)"
                      ) {
                        const text = container.textContent || "";
                        if (text === "\u200B" || text === "\uFEFF") {
                          event.preventDefault();
                          const prevSibling: Node | null =
                            parent.previousSibling;
                          parent.parentNode?.removeChild(parent);
                          if (prevSibling?.nodeType === Node.TEXT_NODE) {
                            const t = prevSibling as Text;
                            if (t.textContent && t.textContent.length > 0) {
                              t.textContent = t.textContent.slice(0, -1);
                              const newRng = editor.dom.createRng();
                              newRng.setStart(t, t.textContent.length);
                              newRng.collapse(true);
                              sel.setRng(newRng);
                            }
                          } else if (
                            prevSibling?.nodeType === Node.ELEMENT_NODE
                          ) {
                            const el = prevSibling as HTMLElement;
                            const last = el.lastChild;
                            if (last?.nodeType === Node.TEXT_NODE) {
                              const lastText = last as Text;
                              if (
                                lastText.textContent &&
                                lastText.textContent.length > 0
                              ) {
                                lastText.textContent =
                                  lastText.textContent.slice(0, -1);
                                const newRng = editor.dom.createRng();
                                newRng.setStart(
                                  lastText,
                                  lastText.textContent.length
                                );
                                newRng.collapse(true);
                                sel.setRng(newRng);
                              }
                            }
                          }
                          return;
                        }
                        if (offset === 0) {
                          event.preventDefault();
                          const prevSibling: Node | null =
                            parent.previousSibling;
                          parent.parentNode?.removeChild(parent);
                          if (prevSibling?.nodeType === Node.TEXT_NODE) {
                            const t = prevSibling as Text;
                            if (t.textContent && t.textContent.length > 0) {
                              t.textContent = t.textContent.slice(0, -1);
                              const newRng = editor.dom.createRng();
                              newRng.setStart(t, t.textContent.length);
                              newRng.collapse(true);
                              sel.setRng(newRng);
                            }
                          } else if (
                            prevSibling?.nodeType === Node.ELEMENT_NODE
                          ) {
                            const el = prevSibling as HTMLElement;
                            const last = el.lastChild;
                            if (last?.nodeType === Node.TEXT_NODE) {
                              const lastText = last as Text;
                              if (
                                lastText.textContent &&
                                lastText.textContent.length > 0
                              ) {
                                lastText.textContent =
                                  lastText.textContent.slice(0, -1);
                                const newRng = editor.dom.createRng();
                                newRng.setStart(
                                  lastText,
                                  lastText.textContent.length
                                );
                                newRng.collapse(true);
                                sel.setRng(newRng);
                              }
                            }
                          }
                          return;
                        }
                      }
                    }

                    if (
                      container.nodeType === Node.ELEMENT_NODE &&
                      offset > 0
                    ) {
                      const el = container as Element;
                      const prev: ChildNode | null = el.childNodes.item(
                        offset - 1
                      );
                      if (prev?.nodeType === Node.ELEMENT_NODE) {
                        const prevEl = prev as HTMLElement;
                        if (
                          prevEl.style?.color === "rgba(0, 0, 0, 0.87)" ||
                          prevEl.style?.color === "rgb(0, 0, 0)"
                        ) {
                          event.preventDefault();
                          const prevSibling: Node | null =
                            prevEl.previousSibling;
                          prevEl.parentNode?.removeChild(prevEl);
                          if (prevSibling?.nodeType === Node.TEXT_NODE) {
                            const t = prevSibling as Text;
                            if (t.textContent && t.textContent.length > 0) {
                              t.textContent = t.textContent.slice(0, -1);
                              const newRng = editor.dom.createRng();
                              newRng.setStart(t, t.textContent.length);
                              newRng.collapse(true);
                              sel.setRng(newRng);
                            }
                          } else if (
                            prevSibling?.nodeType === Node.ELEMENT_NODE
                          ) {
                            const prevSiblingEl = prevSibling as HTMLElement;
                            const last = prevSiblingEl.lastChild;
                            if (last?.nodeType === Node.TEXT_NODE) {
                              const lastText = last as Text;
                              if (
                                lastText.textContent &&
                                lastText.textContent.length > 0
                              ) {
                                lastText.textContent =
                                  lastText.textContent.slice(0, -1);
                                const newRng = editor.dom.createRng();
                                newRng.setStart(
                                  lastText,
                                  lastText.textContent.length
                                );
                                newRng.collapse(true);
                                sel.setRng(newRng);
                              }
                            }
                          }
                          return;
                        }
                      }
                    }

                    if (container.nodeType === Node.TEXT_NODE) {
                      const parent = container.parentNode as HTMLElement | null;
                      if (
                        parent?.style?.color === "rgba(0, 0, 0, 0.87)" ||
                        parent?.style?.color === "rgb(0, 0, 0)"
                      ) {
                        const text = container.textContent || "";
                        if (
                          text === "\u200B" ||
                          text === "\uFEFF" ||
                          offset === 0
                        ) {
                          event.preventDefault();
                          const prevSibling: Node | null =
                            parent.previousSibling;
                          parent.parentNode?.removeChild(parent);
                          if (prevSibling?.nodeType === Node.TEXT_NODE) {
                            const t = prevSibling as Text;
                            if (t.textContent && t.textContent.length > 0) {
                              t.textContent = t.textContent.slice(0, -1);
                              const newRng = editor.dom.createRng();
                              newRng.setStart(t, t.textContent.length);
                              newRng.collapse(true);
                              sel.setRng(newRng);
                            }
                          } else if (
                            prevSibling?.nodeType === Node.ELEMENT_NODE
                          ) {
                            const prevSiblingEl = prevSibling as HTMLElement;
                            const last = prevSiblingEl.lastChild;
                            if (last?.nodeType === Node.TEXT_NODE) {
                              const lastText = last as Text;
                              if (
                                lastText.textContent &&
                                lastText.textContent.length > 0
                              ) {
                                lastText.textContent =
                                  lastText.textContent.slice(0, -1);
                                const newRng = editor.dom.createRng();
                                newRng.setStart(
                                  lastText,
                                  lastText.textContent.length
                                );
                                newRng.collapse(true);
                                sel.setRng(newRng);
                              }
                            }
                          }
                          return;
                        }
                      }
                    }
                  }

                  // > handler
                  if (event.key === ">") {
                    setTimeout(() => {
                      const sel = editor.selection;
                      const node = sel.getNode();
                      let coloredParent: HTMLElement | null = null;
                      let current: Node | null = node;
                      while (current && current !== editor.getBody()) {
                        if (current.nodeType === 1) {
                          const elem = current as HTMLElement;
                          const color = elem.style.color;
                          if (
                            color &&
                            color !== "rgba(0, 0, 0, 0.87)" &&
                            color !== "rgb(0, 0, 0)"
                          ) {
                            coloredParent = elem;
                            break;
                          }
                        }
                        current = current.parentNode;
                      }
                      if (coloredParent) {
                        const neutralSpan = document.createElement("span");
                        neutralSpan.style.color = "rgba(0, 0, 0, 0.87)";
                        neutralSpan.innerHTML = "\u200B";
                        editor.selection.collapse(false);
                        const rng = editor.selection.getRng();
                        rng.insertNode(neutralSpan);
                        const newRng = editor.dom.createRng();
                        newRng.setStart(neutralSpan.firstChild!, 1);
                        newRng.collapse(true);
                        sel.setRng(newRng);
                      }
                    }, 0);
                  }
                });

                editor.on("keypress", (event) =>
                  onEditorKeypress(event as unknown as KeyboardEvent, editor)
                );

                editor.on("SelectionChange", () => {
                  checkSelectionInsideTag();
                  setTimeout(() => {
                    setActiveSSMLTags((prev) => [...prev]);
                  }, 0);
                });

                editor.on("mouseup", () => {
                  checkSelectionInsideTag();
                  setTimeout(() => {
                    setActiveSSMLTags((prev) => [...prev]);
                  }, 0);
                });

                editor.on("keyup", () => {
                  checkSelectionInsideTag();
                  setTimeout(() => {
                    setActiveSSMLTags((prev) => [...prev]);
                  }, 0);
                });

                editor.on("remove", () => {
                  setIsEditorReady(false);
                });
              },
            }}
            onEditorChange={handleEditorChange}
          />
          <div
            style={{
              position: "absolute",
              bottom: "1px",
              left: "1px",
              right: "1px",
              zIndex: 1000,
              pointerEvents: "auto",
            }}
          >
            <div style={{ pointerEvents: "auto" }}>
              <ErrorBox />
            </div>

            {showSSMLBanner && (
              <div style={{ pointerEvents: "auto" }}>
                <SSMLViewBanner
                  isEditing={isEditing}
                  onStartEditing={handleStartEditing}
                />
              </div>
            )}
          </div>

          {shouldShowNestingBanner && !showSSMLBanner && (
            <div
              style={{
                position: "absolute",
                bottom: "1px",
                left: "1px",
                right: "1px",
                zIndex: 1000,
                pointerEvents: "auto",
              }}
            >
              <SSMLNestingBanner />
            </div>
          )}
        </div>

        {/* SSML Validation Failure Notification Dialog */}
        <ValidationFailureNotification
          open={showSSMLNotification}
          onStayInSSML={handleStayInSSML}
          onSwitchToVisual={handleSwitchToVisual}
        />

        {/* SSML Validation Success Notification Dialog */}
        <ValidationSuccessfulNotification
          open={showSuccessNotification}
          onCancel={handleSuccessNotificationClose}
        />
      </>
    );
  }
);

export default TTSCustomEditor;
